import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConnectionAdapter } from "../../../services/ConnectionRegistryProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { useEngageClient } from "./useEngageClient";
import { readMigratedSessionPref } from "../../../shared/sessionPref";
import type { EngageJob } from "../../../chat/services/workflowApi";

export type EngageRequestFilter = "active" | "completed";

// Engage returns the job title ONLY in the active feed (GetUserProcessing) —
// the Status feed and GetJob don't carry it. So we persist a jobNo → title map
// seen in the active feed; once a job completes and drops out of that feed we
// can still show its real title in the Completed tab (AIVA-6988).
const TITLE_CACHE_KEY = "aiva_engage_job_titles";
const TITLE_CACHE_MAX = 500;
// Cap the per-load Search lookups for uncached completed-job titles so a large
// Completed tab can't fan out into hundreds of requests in one go.
const TITLE_LOOKUP_MAX = 30;

function loadTitleCache(): Record<string, string> {
  try {
    // AIVA-6685 (V-08): title cache lives in sessionStorage (taskpane-only);
    // a legacy localStorage value is migrated once. See shared/sessionPref.ts.
    const raw = readMigratedSessionPref(TITLE_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function saveTitleCache(cache: Record<string, string>): void {
  try {
    const entries = Object.entries(cache);
    // Cap so the store can't grow without bound; keep the most recent entries.
    const trimmed = entries.length > TITLE_CACHE_MAX ? entries.slice(-TITLE_CACHE_MAX) : entries;
    globalThis.sessionStorage?.setItem(TITLE_CACHE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
  } catch {
    // Persistence is best-effort — a failure just means titles aren't cached.
  }
}

interface RequestRow {
  readonly jobNo: string;
  readonly title: string;
  readonly requestor: string;
  readonly dueDate: string;
  readonly completed: boolean;
  readonly raw: EngageJob;
}

interface UseEngageRequestsArgs {
  readonly site: string;
  readonly enabled?: boolean;
  // Signed-in user's email — used to scope the (site-wide) completed feed to
  // the current user's jobs. When omitted, completed jobs aren't user-filtered.
  readonly userEmail?: string;
}

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

// The Status feed is a site-wide "since this timestamp" query, so an old start
// returns a huge payload. Limit it to recent history to keep the response small
// while still covering recent completed jobs.
const COMPLETED_LOOKBACK_DAYS = 90;

/** ISO timestamp (no ms/zone, matching the API) `COMPLETED_LOOKBACK_DAYS` ago. */
function completedSince(): string {
  const d = new Date();
  d.setDate(d.getDate() - COMPLETED_LOOKBACK_DAYS);
  return d.toISOString().slice(0, 19);
}

function pickString(row: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return "";
}

// Words in a status/stage value that mean the job is finished.
const COMPLETED_STATUS_RE = /complet|closed|delivered|finished|archiv|\bdone\b/;

// First non-empty string value among the keys matching `keyMatch`. Engage's
// GetUserProcessing/GetProcessing summary rows use inconsistent casing
// (statuS_NAME, joB_STATUS, stagE_NAME…), so we match on key shape rather than
// an exhaustive name list — otherwise a summary row whose status lives under a
// slightly different key is never recognised as complete.
function firstStringByKey(rec: Record<string, unknown>, keyMatch: RegExp): string {
  for (const [k, v] of Object.entries(rec)) {
    if (keyMatch.test(k) && typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return "";
}

// The job's next workflow step (trimmed, lower-cased) from the first string
// "next step" field, or null when the row carries no such field. Engage routes
// jobs through workflow steps and a finished job has none left ("--No Next
// Step--" in the Engage grid), so an empty next step means the job is done.
function nextStepValue(rec: Record<string, unknown>): string | null {
  for (const [k, v] of Object.entries(rec)) {
    if (/next.*step/i.test(k) && typeof v === "string") return v.trim().toLowerCase();
  }
  return null;
}

// A job is "completed" when its status/stage reads as finished, or — mirroring
// the Engage grid, which flags done jobs with "--No Next Step--" — when it has
// no remaining workflow step. Engage has no separate completed endpoint, so we
// classify the jobs returned by GetUserProcessing/GetProcessing.
function isCompleted(rec: Record<string, unknown>): boolean {
  // 1. Explicit status/stage text (statuS_NAME, status, joB_STATUS, stagE_NAME…).
  const status = firstStringByKey(rec, /status|stage/i).toLowerCase();
  if (COMPLETED_STATUS_RE.test(status)) return true;

  // 1b. The Status feed reports the outcome via `action`
  //     (ACCEPTED/STARTED/COMPLETED/CANCELLED…) — "COMPLETED" means finished.
  const action = pickString(rec, ["action", "ACTION"]).toLowerCase();
  if (COMPLETED_STATUS_RE.test(action)) return true;

  // 2. A non-default completed/closed timestamp marks the job finished — the
  //    Status feed carries these on completed rows (Engage's null date is
  //    "0001-01-01…", which we treat as not-completed).
  for (const [k, v] of Object.entries(rec)) {
    if (!/^(completed|closed)$/i.test(k)) continue;
    if (typeof v === "string" && v.trim().length > 0 && !v.startsWith("0001-01-01")) return true;
  }

  // 3. Workflow routing: an empty / "no next step" next-step field means the job
  //    is finished, while a real value (e.g. "Quality Control") means it is still
  //    active. Only used when the row actually carries a next-step field, so a
  //    payload that omits it never regresses to a false "completed".
  const nextStep = nextStepValue(rec);
  if (nextStep !== null) return nextStep === "" || nextStep.includes("no next step");

  return false;
}

function toRow(job: EngageJob): RequestRow {
  const rec = job as Record<string, unknown>;
  const jobNo = pickString(rec, ["joB_NO", "JOB_NO", "job_no"]);
  // The Status feed carries no joB_TITLE — fall back to the job type. The
  // requestor there is `createD_BY`, so include it as a fallback too.
  const title =
    pickString(rec, ["joB_TITLE", "JOB_TITLE", "title", "joB_TYPE", "JOB_TYPE"]) || "Untitled";
  const requestor = pickString(rec, [
    "requesteD_BY",
    "REQUESTED_BY",
    "requested_by",
    "requestor",
    "createD_BY",
    "CREATED_BY",
  ]);
  const dueRaw = pickString(rec, ["duE_DATE", "DUE_DATE", "due_date"]);
  let dueDate = "";
  if (dueRaw) {
    const t = Date.parse(dueRaw);
    dueDate = Number.isNaN(t) ? dueRaw : DATE_FMT.format(new Date(t));
  }
  return { jobNo, title, requestor, dueDate, completed: isCompleted(rec), raw: job };
}

function matchSearch(row: RequestRow, search: string): boolean {
  if (!search) return true;
  const q = search.toLowerCase();
  return (
    row.jobNo.toLowerCase().includes(q) ||
    row.title.toLowerCase().includes(q) ||
    row.requestor.toLowerCase().includes(q)
  );
}

// Does a Status-feed row's `createD_BY` (an Engage username like
// "abirami.moorthy") belong to the signed-in user? The Status feed is
// site-wide, so we scope My Requests to the current user. With no user context
// we don't filter (show all) rather than hide everything.
function matchesUser(createdBy: string, userEmail: string): boolean {
  const email = userEmail.trim().toLowerCase();
  if (!email) return true;
  const cb = createdBy.trim().toLowerCase();
  if (!cb) return false;
  // createD_BY is an Engage username ("boopathiraja.selvam") or a full email;
  // compare on the local part so both forms match the signed-in user.
  const emailLocal = (email.split("@")[0] ?? email).trim();
  const cbLocal = (cb.split("@")[0] ?? cb).trim();
  return cbLocal === emailLocal;
}

/**
 * Build completed-job rows from the Status event feed. The feed is one row per
 * status change (ACCEPTED/STARTED/COMPLETED/…), many per job, so we take the
 * LATEST event per job and keep those whose latest action is COMPLETED — this
 * naturally drops cancelled jobs and ones re-opened after completion. Scoped to
 * the current user via `createD_BY`.
 */
function completedRowsFromStatus(events: readonly EngageJob[], userEmail: string): RequestRow[] {
  const latest = new Map<string, EngageJob>();
  for (const ev of events) {
    const rec = ev as Record<string, unknown>;
    const jobNo = pickString(rec, ["joB_NO", "JOB_NO", "job_no"]);
    if (!jobNo) continue;
    const created = pickString(rec, ["created", "CREATED"]);
    const prev = latest.get(jobNo);
    const prevCreated = prev ? pickString(prev as Record<string, unknown>, ["created", "CREATED"]) : "";
    // ISO timestamps compare correctly as strings.
    if (!prev || created > prevCreated) latest.set(jobNo, ev);
  }

  const rows: RequestRow[] = [];
  for (const ev of latest.values()) {
    const rec = ev as Record<string, unknown>;
    if (pickString(rec, ["action", "ACTION"]).toUpperCase() !== "COMPLETED") continue;
    if (!matchesUser(pickString(rec, ["createD_BY", "CREATED_BY"]), userEmail)) continue;
    rows.push(toRow(ev));
  }
  return rows;
}

export function useEngageRequests({ site, enabled = true, userEmail = "" }: UseEngageRequestsArgs) {
  const adapter = useConnectionAdapter("engage");
  const client = useEngageClient();

  const [filter, setFilter] = useState<EngageRequestFilter>("active");
  const [search, setSearch] = useState("");
  const [activeItems, setActiveItems] = useState<EngageJob[]>([]);
  const [completedItems, setCompletedItems] = useState<EngageJob[]>([]);
  // jobNo → title map, seeded from localStorage and topped up from each active
  // feed, so completed jobs (whose feeds carry no title) still show one (AIVA-6988).
  const [titleCache, setTitleCache] = useState<Record<string, string>>(() => loadTitleCache());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connected = adapter?.isConnected() ?? false;
  const canLoad = enabled && connected && site.length > 0;
  const reqIdRef = useRef(0);
  // Mirror the cache in a ref so the async completed-title hydration can skip
  // jobs already titled without re-subscribing to titleCache.
  const titleCacheRef = useRef(titleCache);
  useEffect(() => {
    titleCacheRef.current = titleCache;
  }, [titleCache]);

  // Fold any joB_TITLE found on the given job records into the persisted cache.
  const mergeTitles = useCallback((jobs: ReadonlyArray<Record<string, unknown>>) => {
    setTitleCache((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const rec of jobs) {
        const no = pickString(rec, ["joB_NO", "JOB_NO", "job_no"]);
        const t = pickString(rec, ["joB_TITLE", "JOB_TITLE", "title"]);
        if (no && t && next[no] !== t) {
          next[no] = t;
          changed = true;
        }
      }
      if (changed) saveTitleCache(next);
      return changed ? next : prev;
    });
  }, []);

  // Fetch only the Active feed: prefer GetUserProcessing (already scoped to the
  // signed-in user), falling back to GetProcessing/{site}. Titles seen here are
  // folded into the cache so they survive into the Completed tab (AIVA-6988).
  const loadActive = useCallback(
    async (id: number) => {
      const active = await (async () => {
        try {
          return await client.getUserProcessing();
        } catch {
          return await client.getProcessing(site);
        }
      })();
      const activeList = Array.isArray(active) ? active : [];
      // Fold titles into the cache even if this load was superseded (e.g. the
      // user switched to Completed mid-flight) — it's an idempotent cache write,
      // and it's how titles seen active seed the Completed tab (AIVA-6988).
      mergeTitles(activeList);
      if (id !== reqIdRef.current) return;
      setActiveItems(activeList);
    },
    [client, site, mergeTitles],
  );

  // Fetch only the Completed feed from the site-wide Status event feed (finished
  // jobs aren't returned by the active endpoints).
  const loadCompleted = useCallback(
    async (id: number) => {
      const completed = await client.getStatus(site, completedSince());
      if (id !== reqIdRef.current) return;
      const completedList = Array.isArray(completed) ? completed : [];
      setCompletedItems(completedList);

      // Recover titles for completed jobs we've never seen active: the Search
      // endpoint returns joB_TITLE for ANY job (finished included), unlike the
      // Status feed. Best-effort and unawaited so the tab isn't blocked; a
      // per-job failure just leaves that row on its job-type fallback.
      void (async () => {
        const missing = completedRowsFromStatus(completedList, userEmail)
          .map((r) => r.jobNo)
          .filter((no) => no && !titleCacheRef.current[no])
          .slice(0, TITLE_LOOKUP_MAX);
        if (missing.length === 0) return;
        const found = await Promise.all(
          missing.map(async (no) => {
            try {
              const rec = (await client.searchJob(no)) as Record<string, unknown> | null;
              const t = rec ? pickString(rec, ["joB_TITLE", "JOB_TITLE", "title"]) : "";
              return t ? ([no, t] as const) : null;
            } catch {
              return null;
            }
          }),
        );
        if (id !== reqIdRef.current) return;
        mergeTitles(
          found.filter((e): e is readonly [string, string] => e !== null).map(([no, t]) => ({ joB_NO: no, joB_TITLE: t })),
        );
      })();
    },
    [client, site, userEmail, mergeTitles],
  );

  // Load only the feed for the requested tab — each tab makes its own API call,
  // re-fetched when the user switches to it (rather than fetching both up front).
  const load = useCallback(
    async (which: EngageRequestFilter) => {
      const id = ++reqIdRef.current;
      if (!canLoad) {
        if (id === reqIdRef.current) {
          setActiveItems([]);
          setCompletedItems([]);
        }
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (which === "completed") await loadCompleted(id);
        else await loadActive(id);
        logConnectorSuccess("engage", "load-requests");
      } catch (e) {
        if (id !== reqIdRef.current) return;
        const msg = e instanceof Error ? e.message : "Failed to load requests";
        setError(msg);
        logConnectorFailure("engage", e, "load-requests");
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    },
    [canLoad, loadActive, loadCompleted],
  );

  // Fetch the current tab's feed on mount and whenever the selected tab changes.
  useEffect(() => {
    load(filter).catch(() => undefined);
  }, [load, filter]);

  const reload = useCallback(() => load(filter), [load, filter]);

  const rows = useMemo(() => {
    const q = search.trim();
    // Completed jobs come from the Status event feed (latest action COMPLETED),
    // scoped to the current user. Show the real Job Title from the title cache
    // (falling back to the job type when a job was never seen active), and only
    // #JobNo + Title — drop the requestor email / due date the Status feed
    // carried, which belong in the job's detail view (AIVA-6988).
    const completedRows = completedRowsFromStatus(completedItems, userEmail).map((r) => ({
      ...r,
      title: titleCache[r.jobNo] || r.title,
      requestor: "",
      dueDate: "",
    }));
    const completedNos = new Set(completedRows.map((r) => r.jobNo).filter(Boolean));
    // Active jobs from the processing feed, minus anything already completed so
    // a just-finished job never shows under both tabs.
    const activeRows = activeItems
      .map(toRow)
      .filter((r) => !r.completed && !completedNos.has(r.jobNo));

    const source = filter === "completed" ? completedRows : activeRows;
    return source.filter((r) => matchSearch(r, q));
  }, [activeItems, completedItems, titleCache, filter, search, userEmail]);

  return {
    rows,
    filter,
    setFilter,
    search,
    setSearch,
    loading,
    error,
    reload,
    connected,
  };
}

export type EngageRequestRow = RequestRow;
